package com.eladfinish.bible.contest.screens.chapterComplete.usecases

import com.eladfinish.bible.contest.domain.BooksRepository
import com.eladfinish.bible.contest.screens.browse.tableOfContents.model.OutputChapters
import com.eladfinish.bible.contest.screens.browse.usecases.GetTableRowsUseCase
import com.eladfinish.bible.contest.screens.chapterComplete.usecases.BuildLexicographicalTableHtmlUseCase.Companion.PREVIEW_HEAD
import com.eladfinish.bible.contest.screens.chapterComplete.usecases.BuildLexicographicalTableHtmlUseCase.Companion.PREVIEW_TAIL
import com.eladfinish.bible.contest.screens.lexicographicalSort.model.LexicographicalTable
import com.eladfinish.bible.contest.screens.lexicographicalSort.model.LexicographicalTableLabels
import com.eladfinish.bible.contest.screens.lexicographicalSort.model.toTableRows
import com.eladfinish.bible.contest.screens.lexicographicalSort.usecases.SortLexicographicallyWebViewUseCase

class BuildLexicographicalTableHtmlUseCase(
    private val sortVersesSingleChapterUseCase: SortVersesSingleChapterUseCase,
    private val sortVersesByMikudUseCase: SortVersesByMikudUseCase,
    private val sortVersesFullBookUseCase: SortVersesFullBookUseCase,
    private val sortVersesLexicographicallyUseCase: SortVersesLexicographicallyUseCase,
    private val sortLexicographicallyWebViewUseCase: SortLexicographicallyWebViewUseCase,
    private val getTableRowsUseCase: GetTableRowsUseCase,
) {

    /**
     * @param full the complete table HTML (used for the saved/emailed PDF).
     * @param preview a trimmed teaser for the in-app WebView: first [PREVIEW_HEAD] rows, a
     *   "more verses" divider, and the last [PREVIEW_TAIL] rows. Equals [full] when the table
     *   is small enough that there is nothing to hide.
     * @param previewTable the same rows as [preview], as data, for the platform that renders
     *   the preview natively instead of in a WebView.
     */
    data class Result(val full: String, val preview: String, val previewTable: LexicographicalTable)

    suspend operator fun invoke(
        outputChapters: OutputChapters,
        indexPickedBook: Int,
        indexPickedChapter: Int,
    ): Result {
        val verseEntities =
            when (outputChapters) {
                OutputChapters.SINGLE -> {
                    sortVersesSingleChapterUseCase(
                        bookNumber = indexPickedBook,
                        chapterNumber = indexPickedChapter,
                    )
                }

                OutputChapters.BY_MIKUD -> {
                    sortVersesByMikudUseCase(bookNumber = indexPickedBook)
                }

                OutputChapters.ALL -> {
                    sortVersesFullBookUseCase(bookNumber = indexPickedBook)
                }
            }

        val sortedVerses = sortVersesLexicographicallyUseCase(versesList = verseEntities)
        val total = sortedVerses.size
        val hebrewBookName = BooksRepository.bookNamesHebrew[indexPickedBook - 1]

        fun wrap(rows: String): String =
            sortLexicographicallyWebViewUseCase(
                hebrewBookName = hebrewBookName,
                chapterNum = indexPickedChapter,
                data = rows,
                outputChapters = outputChapters,
                verseCount = total,
            )

        fun table(rows: List<LexicographicalTable.Row>) = LexicographicalTable(
            place = sortLexicographicallyWebViewUseCase.place(hebrewBookName, indexPickedChapter, outputChapters),
            verseCount = total,
            speakerTitle = sortLexicographicallyWebViewUseCase.speakerTitle(hebrewBookName),
            rows = rows,
        )

        val full = wrap(getTableRowsUseCase(verseEntities = sortedVerses))

        // A single chapter is short enough to show in full — no teaser trimming.
        if (outputChapters == OutputChapters.SINGLE || total <= PREVIEW_HEAD + PREVIEW_TAIL) {
            return Result(full = full, preview = full, previewTable = table(sortedVerses.toTableRows()))
        }

        val headVerses = sortedVerses.take(PREVIEW_HEAD)
        val tailVerses = sortedVerses.takeLast(PREVIEW_TAIL)
        val tailStartNumber = total - PREVIEW_TAIL + 1
        val teaser = LexicographicalTableLabels.hiddenVerses(hidden = total - PREVIEW_HEAD - PREVIEW_TAIL)

        return Result(
            full = full,
            preview = wrap(
                getTableRowsUseCase(headVerses, startNumber = 1) +
                        teaserRow(teaser) +
                        getTableRowsUseCase(tailVerses, startNumber = tailStartNumber)
            ),
            previewTable = table(
                headVerses.toTableRows(startNumber = 1) +
                        LexicographicalTable.Row.Teaser(teaser) +
                        tailVerses.toTableRows(startNumber = tailStartNumber)
            ),
        )
    }

    /** A divider row that hides the middle of the table and hints at how much more there is. */
    private fun teaserRow(text: String): String =
        """
        <tr class="preview-teaser">
            <td colspan="7">
                ⋮<br/>$text<br/>⋮
            </td>
        </tr>
        """.trimIndent()

    private companion object {
        const val PREVIEW_HEAD = 10
        const val PREVIEW_TAIL = 10
    }

}
